import { Component, EventEmitter, Input, OnChanges, OnInit, Output, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { TranslocoService } from '@ngneat/transloco';
import { SelectItem } from 'primeng/api';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { PRIORTIES_2 } from 'projects/core/src/lib/constants';
import { LanguageModel } from 'projects/core/src/lib/models/language.model';
import { LocationModel } from 'projects/core/src/lib/models/location.model';
import { UserModel } from 'projects/core/src/lib/models/user.model';
import { forkJoin, Subscription } from 'rxjs';

enum ViewMode {
  Admin = 'admin',
  Profile = 'profile'
}

@Component({
  selector: 'app-user-locations',
  templateUrl: './user-locations.component.html',
  styleUrls: ['./user-locations.component.scss']
})
export class UserLocationsComponent extends BaseForm implements OnInit, OnChanges, OnDestroy {

  @Input() user: UserModel = null;
  @Input() viewMode: ViewMode = ViewMode.Admin;
  @Output() setEdit = new EventEmitter<string>();

  public loading = false;
  public languages: Array<LanguageModel> = [];
  public locations: Array<LocationModel> = [];
  public priorities: Array<SelectItem> = [];
  public subscriptions: Array<Subscription> = [];

  public editRight = 'admin.user.edit';

  public form = this.fb.group({
    additional_locations: new UntypedFormControl('')
  });

  constructor(
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private api: ApiService,
    private transloco: TranslocoService,
    private auth: AuthStateService,
    private helper: HelperService
  ) {
    super();
  }

  ngOnInit(): void {
    this.loading = true;
    this.priorities = FormHelper.getSelectList(PRIORTIES_2);
    this.setRights();
    forkJoin([
      this.api.getModels('languages'),
      this.api.getModels('locations'),
    ]).subscribe({
      next: (result: any) => {
        if (result[0]?.data?.length > 0) {
          this.languages = result[0].data.map((language: any) => {
            const lang = new LanguageModel().deserialize(language);
            lang.control = new UntypedFormControl(false);
            this.form.addControl(lang.name, lang.control);
            return lang;
          })
        }
        this.setLanguages();
        if (result[1]?.data?.length > 0) {
          this.locations = result[1].data.map((location: any) => {
            const loc = new LocationModel().deserialize(location);
            loc.control_value = new UntypedFormControl(false);
            loc.control_priority = new UntypedFormControl({value: null, disabled: true});
            this.form.addControl(loc.name + '_value', loc.control_value);
            this.form.addControl(loc.name + '_priority', loc.control_priority);
            this.subscriptions.push(loc.control_value.valueChanges.subscribe((value: any) => {
              if (value === true) {
                loc.control_priority.setValidators([Validators.required]);
                loc.control_priority.enable();
                loc.control_priority.setValue(this.priorities[0]?.value);
              } else {
                loc.control_priority.clearValidators();
                loc.control_priority.disable();
                loc.control_priority.setValue(null);
                loc.control_priority.markAsPristine();
                loc.control_priority.markAsUntouched();
              }
              loc.control_priority.updateValueAndValidity();
            }));
            return loc;
          })
        }
        this.setLocations();
        this.setAdditionalLocations();
      },
      error: (error: any) => {}
    }).add(() => {
      this.loading = false;
    });
  }

  ngOnChanges(): void {
    this.setLanguages();
    this.setLocations();
    this.setAdditionalLocations();
  }

  ngOnDestroy(): void {
    this.helper.unsetSubscriptions(this.subscriptions);
  }

  onSubmit(): void {
    this.ui.hideMessage();
    if (this.form.valid === true) {
      this.submitting = true;
      const payload: any = {
        languages: this.getLanguagesPayload(),
        locations: this.getLoationsPayload(),
        additional_locations: this.form.get('additional_locations').value
      };
      const request = (this.viewMode === ViewMode.Profile) ? this.api.saveMyLocations(payload) : this.api.saveUserLocations(this.user.id, payload);
      request.subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.saved'), this.transloco.translate('common.saved'));
          this.user.deserialize(result.data);
          this.stopEdit();
        },
        error: (error: any) => {
          this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          FormHelper.setBackendErrors(this.form, error.fielderrors);
        }
      }).add(() => {
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  private setRights(): void {
    if (this.viewMode === ViewMode.Profile && this.auth.hasRole('moderator')) {
      this.editRight = null;
    }
  }

  protected startEdit(): void {
    if (this.auth.hasRight(this.editRight)) {
      if (this.user !== null) {
        this.form.patchValue({});
      }
      this.setEdit.emit('locations');
      super.startEdit();
      this.ui.setFormFocus();
    }
  }

  protected stopEdit(): void {
    this.form.reset();
    this.setEdit.emit(null);
    super.stopEdit();
    this.ui.setFocus();
    this.setLanguages();
    this.setLocations();
    this.setAdditionalLocations();
  }

  private setLanguages(): void {
    if (this.user !== null) {
      this.languages.forEach((language: LanguageModel) => {
        language.control.setValue(this.user.languages?.find((actual: LanguageModel) => {
          return actual.id === language.id;
        }) !== undefined ? true : false);
      });
    }
  }

  private setLocations(): void {
    if (this.user !== null) {
      this.locations.forEach((location: LocationModel) => {
        const found: LocationModel = this.user.locations?.find((actual: LocationModel) => {
          return actual.id === location.id;
        });
        const priority = this.priorities.find((actual: SelectItem) => {
          return actual.value == found?.pivot?.priority;
        });
        location.control_value.setValue(found !== undefined ? true : false);
        location.control_priority.setValue(priority !== undefined ? priority.value : null);
      });
    }
  }

  private setAdditionalLocations(): void {
    if (this.user !== null) {
      this.form.get('additional_locations').setValue(this.user.getMisc('additional_locations'));
    }
  }

  private getLanguagesPayload(): Array<number> {
    const languages: Array<number> = [];
    this.languages.forEach((language: LanguageModel) => {
      if (language.control.value === true) {
        languages.push(language.id);
      }
    });
    return languages;
  }

  private getLoationsPayload(): any {
    const locations: any = {};
    this.locations.forEach((location: LocationModel) => {
      if (location.control_value.value === true) {
        locations[location.id.toString()] = {
          priority: location.control_priority.value
        };
      }
    });
    return locations;
  }

}
